import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import { AiFillSave } from 'react-icons/ai';

import {
    QUERIES_ADMIN,
    ROUTES_ACCOUNT,
    ROUTES_ADMIN,
} from '@constants';

import { useRealState } from 'contexts/RealStateContext';

import { Button, Loading, Shadow } from 'components/atoms';
import { OptionBlock, InputText } from 'components/molecules';

import { onErrorMessage, onSuccessMessage, onValidationSchema } from 'helpers';

import {
    createAdministratorValueDetails,
    getAdministratorValueDetailsShow,
    updateAdministratorValueDetails,
} from 'services';

import { ValueDetailsFormWrap } from './styles';
import Layout from '../../Layout';

import type { IAValueDetail } from '@types';

const ValueDetailsForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const queryClient = useQueryClient();

    const { getLinkWithSlug } = useRealState();

    const [step, setStep] = useState('GENERAL_INFO');

    const [nome, setNome] = useState('');

    const titleMessages = 'Detalhes de Valores!';

    const { data: dataDetail, isLoading: isLoadingGet } = useQuery(
        [QUERIES_ADMIN.VALUE_DETAIL.base, id],
        () => getAdministratorValueDetailsShow(String(id)),
        {
            enabled: !!id,
        }
    );

    useEffect(() => {
        if (dataDetail) {
            setNome(dataDetail?.nome);
        }
    }, [dataDetail]);

    const { mutate: mutateCreate, isLoading: isLoadingCreate } = useMutation(
        (payload: Partial<IAValueDetail>) => createAdministratorValueDetails(payload),
        {
            mutationKey: [QUERIES_ADMIN.VALUE_DETAIL.base, id],
            onSuccess: () => {
                onSuccessMessage(
                    titleMessages,
                    'Sucesso ao criar novo registro!'
                );

                queryClient.refetchQueries(QUERIES_ADMIN.VALUE_DETAIL.base);

                navigate(getLinkWithSlug(ROUTES_ADMIN.VALUE_DETAIL.fullPath));
            },
            onError: (err) => onErrorMessage(titleMessages, err),
        }
    );

    const { mutate: mutateUpdate, isLoading: isLoadingUpdate } = useMutation(
        (payload: Partial<IAValueDetail>) => updateAdministratorValueDetails(String(id), payload),
        {
            mutationKey: [QUERIES_ADMIN.VALUE_DETAIL.base, id],
            onSuccess: () => {
                onSuccessMessage(
                    titleMessages,
                    'Sucesso ao atualizar o registro!'
                );

                queryClient.refetchQueries(QUERIES_ADMIN.VALUE_DETAIL.base);
                queryClient.refetchQueries([QUERIES_ADMIN.VALUE_DETAIL.base, id]);
            },
            onError: (err) => {
                onErrorMessage(titleMessages, err);
            },
        }
    );

    const onSubmit = async () => {
        const data = {
            nome,
        };

        const shapeSchema = {
            nome: Yup.string().nullable().required('O nome é obrigatório.'),
        };

        const { success } = await onValidationSchema(shapeSchema, data);

        if (success) {
            if (!id) {
                mutateCreate(data);
            } else {
                mutateUpdate(data);
            }
        }
    };

    return (
        <Layout
            titlePage="Painel Administrativo"
            breadcrumbs={[
                {
                    label: 'Home',
                    href: getLinkWithSlug(ROUTES_ACCOUNT.SELECTED.fullPath),
                },
                {
                    label: 'Detalhes de Valores',
                    href: getLinkWithSlug(ROUTES_ADMIN.VALUE_DETAIL.fullPath),
                },
                {
                    label: 'Formulário Detalhes de Valores',
                    href: getLinkWithSlug(ROUTES_ADMIN.VALUE_DETAIL.FORM.fullPath),
                },
            ]}
            title="Formulário Detalhes de Valores"
            buttonTitle={
                <Button color="denary" onClick={() => onSubmit()} loading={isLoadingGet || isLoadingUpdate || isLoadingCreate}>
                    <AiFillSave />
                    Salvar
                </Button>
            }
        >
            <ValueDetailsFormWrap className="administrator-panel-values-details-form">
                <Shadow>
                    <Loading active={isLoadingGet || isLoadingUpdate || isLoadingCreate} />

                    <OptionBlock
                        active={step}
                        onHandleActive={(param) => setStep(param)}
                        data={[
                            {
                                label: 'Informações Gerais',
                                value: 'GENERAL_INFO',
                            },
                        ]}
                        style={{ margin: '0 auto 0 0' }}
                    />

                    <InputText
                        model="sinary"
                        id="nome"
                        required
                        label="Nome"
                        name="nome"
                        placeholder="Informe o nome"
                        value={nome}
                        onChange={setNome}
                    />

                </Shadow>
            </ValueDetailsFormWrap>
        </Layout>
    );
};

export default ValueDetailsForm;
